import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import { getVideoRewardConfig, updateVideoRewardConfig } from '../Api';
import VideoRewardConfigDto from '../models/dtos/VideoRewardConfigDto';

interface VideoRewardConfigModalProps {
    open: boolean;
    onClose: () => void;
    onSave?: () => void;
}

const VideoRewardConfigModal: React.FC<VideoRewardConfigModalProps> = ({ open, onClose, onSave }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    
    const [maxVideosPerDayForUser, setMaxVideosPerDayForUser] = useState<number>(0);

    useEffect(() => {
        if (open) {
            loadConfiguration();
        }
    }, [open]);

    const loadConfiguration = async () => {
        setLoading(true);
        setError(null);
        try {
            const configData = await getVideoRewardConfig();

            if (configData) {
                setMaxVideosPerDayForUser(configData.maxVideosPerDayForUser || 0);
            }
        } catch (err) {
            setError('Failed to load configuration');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        // Validate range
        if (maxVideosPerDayForUser < 0 || maxVideosPerDayForUser > 1000) {
            setError('Max videos per day must be between 0 and 1000');
            return;
        }

        setSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const result = await updateVideoRewardConfig(maxVideosPerDayForUser);

            if (result) {
                setSuccessMessage('Configuration updated successfully');
                setTimeout(() => {
                    if (onSave) onSave();
                    onClose();
                }, 1500);
            } else {
                setError('Failed to update configuration');
            }
        } catch (err) {
            setError('An error occurred while saving');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setError(null);
        setSuccessMessage(null);
        onClose();
    };

    const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value) || 0;
        setMaxVideosPerDayForUser(value);
        
        // Clear error when user corrects the value
        if (value >= 0 && value <= 1000) {
            setError(null);
        }
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Video Reward Configuration</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="150px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        {error && <Alert severity="error">{error}</Alert>}
                        {successMessage && <Alert severity="success">{successMessage}</Alert>}

                        <TextField
                            label="Max Videos Per Day For User"
                            type="number"
                            value={maxVideosPerDayForUser}
                            onChange={handleValueChange}
                            fullWidth
                            required
                            inputProps={{ min: 0, max: 1000 }}
                            helperText="Maximum number of videos a user can watch per day (0-1000)"
                            error={maxVideosPerDayForUser < 0 || maxVideosPerDayForUser > 1000}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    color="primary"
                    disabled={loading || saving || maxVideosPerDayForUser < 0 || maxVideosPerDayForUser > 1000}
                >
                    {saving ? <CircularProgress size={24} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default VideoRewardConfigModal;
